import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { APIError, APIErrorType, getAPIErrorCode, queryKeys, useDeleteImageFromCollectionMutation } from '@vizit/api';
import { IMAGE_PAYLOAD_QUERY_KEYS } from '@vizit/api';
import { collectionQueryPredicate } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { ACTIVE_PDP_DELETE_REASONS, ACTIVE_PDP_ERROR_CODES } from '@vizit/constants';
import * as React from 'react';

/** Why the API refused to delete one image, for the failure toast. */
function describeRefusedDelete(error: unknown) {
  return getAPIErrorCode(error) === ACTIVE_PDP_ERROR_CODES.image
    ? `${ACTIVE_PDP_DELETE_REASONS.image}, so it stays.`
    : 'Nothing was deleted. Please try again.';
}

const DELETED_IMAGES_QUERY_KEYS = new Set([
  ...IMAGE_PAYLOAD_QUERY_KEYS,
  'SearchImagesChildCount',
  'DescendantImageCount',
  'SearchCollections',
  'SearchCollectionsInfinite',
]);

/**
 * Refetches everything that shows images deleted from a collection. Deleting is permanent (SDD D55), so besides the
 * collection and its scorings it also covers the Asset Library of every workspace, Spark images made from them, the
 * collections list counts and cached image details. Shared by single and bulk delete.
 */
async function invalidateDeletedCollectionImages(
  queryClient: QueryClient,
  { workspaceId, collectionId }: { workspaceId: string; collectionId: string },
) {
  // The collection's scorings: their image counts and progress cover the deleted images
  await queryClient.invalidateQueries({
    predicate: collectionQueryPredicate(['ListCollectionScorings'], collectionId),
  });

  // Collection detail, for the count
  await queryClient.invalidateQueries({
    queryKey: queryKeys.collections.detail({ workspace_id: workspaceId, collection_id: collectionId }).queryKey,
  });

  await queryClient.invalidateQueries({
    predicate: (query) => DELETED_IMAGES_QUERY_KEYS.has(String(query.queryKey[0])),
  });
}

interface UseRemoveAssetFromCollectionProps {
  /**
   * The portfolio id
   */
  workspaceId: string;
  /**
   * The collection id
   */
  collectionId: string;
  /**
   * The function to call when an authentication error occurs
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
}

/**
 * A hook to remove an asset from a collection
 * @param props - The props for the hook
 * @returns The hook
 */
function useRemoveAssetFromCollection(props: UseRemoveAssetFromCollectionProps) {
  const { workspaceId, collectionId, onAuthenticationError } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [imageIdToRemove, setImageIdToRemove] = React.useState<string | null>(null);

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { mutateAsync } = useDeleteImageFromCollectionMutation();

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const handleRemoveImageDialogOpen = React.useCallback((id: string) => {
    setImageIdToRemove(id);
    setIsOpen(true);
  }, []);

  const handleRemove = React.useCallback(
    async (id: string) => {
      try {
        await mutateAsync({
          workspace_id: workspaceId,
          collection_id: collectionId,
          image_id: id,
        });

        await invalidateDeletedCollectionImages(queryClient, { workspaceId, collectionId });
      } catch (error) {
        onAuthenticationError?.(error);
        // The API refuses to delete an image that is hero or carousel on an active product page, the bulk Delete says
        // so in its toast. This path used to close the dialog without a word, and with the Actions Bar on (Oct 21
        // release, BLU-369 check on dev1) the tile Delete is in front of users, so it says why too. An expired
        // session has its own handling above.
        if (!(error instanceof APIError && error.type === APIErrorType.NotAuthenticated)) {
          addToast({
            title: 'Could not delete the image',
            description: describeRefusedDelete(error),
            level: AlertLevel.Error,
          });
        }
      }

      setImageIdToRemove(null);
      setIsOpen(false);
    },
    [workspaceId, collectionId, queryClient, mutateAsync, onAuthenticationError, addToast],
  );

  return {
    isOpen,
    imageIdToRemove,
    onOpenChange: handleOpenChange,
    onRemoveImageDialogOpen: handleRemoveImageDialogOpen,
    onRemove: handleRemove,
  };
}

export { invalidateDeletedCollectionImages, useRemoveAssetFromCollection };
